import { ChangeDetectionStrategy, Component, Inject, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, combineLatest, Subject } from 'rxjs';
import { map, filter, tap, take } from 'rxjs/operators';
import { NotebookTypes, CellExportFacade, NotebookCellFacade } from '@mode/notebooks/contract';
import { NormalizedNotebookCellExRes, NormalizedNotebookCellRunResult } from '@mode/notebooks/contract';
import { SessionResource } from '@mode/notebooks/contract';
import { FeatureFlagsFacade, FeatureFlag, DataViewTypes } from '@mode/shared/contract-common';
import { Store } from '@ngrx/store';
import { NotebookStateService } from '@mode/notebooks/domain';
import * as LegacyNotebookCellSelectors from '@mode/notebooks/domain';
@Component({
  selector: 'mode-notebook-cells-group',
  templateUrl: './notebook-cells-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookCellGroupComponent implements OnInit, OnDestroy {
  @Input() cellToken!: string;
  @Input() notebookToken!: string;
  @Input() session?: SessionResource;
  @Input() readOnly = false;

  cellContentType!: NotebookTypes.ContentType;

  // Observables
  results$!: Observable<NormalizedNotebookCellRunResult[]>;
  executionCount$!: Observable<number>;
  showResult$!: Observable<boolean>;
  hasResult$!: Observable<boolean>;
  cell$!: Observable<NormalizedNotebookCellExRes>;
  cellExecuting$!: Observable<boolean>;
  cellToken$!: Observable<string>;
  contentType$!: Observable<NotebookTypes.ContentType>;
  nextCellToken$!: Observable<string>;
  prevCellToken$!: Observable<string>;
  isInLayout$!: Observable<boolean>;
  isEditing$!: Observable<boolean>;
  isSelected$!: Observable<boolean>;
  isFrozen$!: Observable<boolean>;
  isFolded$!: Observable<boolean>;
  canFreeze$!: Observable<boolean>;
  canRunCell$!: Observable<boolean>;
  canDelete$!: Observable<boolean>;
  hasOutput$!: Observable<boolean>;
  isExportDatasetsEnabled$!: Observable<boolean>;
  isCellExported$!: Observable<boolean>;
  exportedDatasetResult: DataViewTypes.DatasetResult | null = null;

  readonly EXPORT_TOOLTIP_HTML_CONTENT = `
    <div class="indicator-tooltip-go-to-dataset">
      <div class='indicator-tooltip'>Output is in use as a local dataset in this Report</div>
      <button class="go-to-dataset-button">Go to Dataset</button>
    </div>`;
  tooltipButtonSelectors = ['.go-to-dataset-button'];

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private notebookCellFacade: NotebookCellFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private notebookStateService: NotebookStateService,
    private cellExportFacade: CellExportFacade,
    @Inject('$state') private $state: any
  ) {}

  ngOnInit() {
    this.cell$ = this.store.select(LegacyNotebookCellSelectors.createNormalizedCellExResSelector(this.cellToken)).pipe(
      filter((c) => !!c),
      tap((cell) => (this.cellContentType = cell.content_type))
    );
    this.hasResult$ = this.notebookStateService.hasResult$;
    this.executionCount$ = this.store.select(
      LegacyNotebookCellSelectors.createCellExecutionCountSelector(this.cellToken)
    );
    this.hasOutput$ = this.notebookStateService.hasOutput$;

    this.results$ = this.store
      .select(LegacyNotebookCellSelectors.createLastResultsByCellSelector(this.cellToken))
      .pipe(filter((r) => r !== null));
    this.isInLayout$ = this.store.select(LegacyNotebookCellSelectors.createIsCellInLayoutSelector(this.cellToken));
    this.isEditing$ = this.store.select(LegacyNotebookCellSelectors.createIsCellEditingSelector(this.cellToken));
    this.isSelected$ = combineLatest([
      this.store.select(LegacyNotebookCellSelectors.selectedCellToken),
      this.cell$,
    ]).pipe(map(([selectedCellToken, cell]) => !this.readOnly && cell.token === selectedCellToken));

    this.showResult$ = combineLatest([
      this.results$,
      this.cell$,
      this.notebookStateService.isCellOutputCleared$(this.cellToken),
    ]).pipe(
      map(
        ([results, c, isCellCleared]) =>
          c.content_type === NotebookTypes.ContentType.Python && results.length > 0 && !c.folded && !isCellCleared
      )
    );

    this.cellExecuting$ = this.notebookStateService.cellExecuting$;
    this.canRunCell$ = this.notebookStateService.canRunCell$(this.notebookToken);
    this.cellToken$ = this.notebookStateService.cellToken$;
    this.nextCellToken$ = this.notebookStateService.nextCellToken$;
    this.prevCellToken$ = this.notebookStateService.prevCellToken$;
    this.isFrozen$ = this.notebookStateService.isFrozen$;
    this.isFolded$ = this.notebookStateService.isFolded$;
    this.canFreeze$ = this.notebookStateService.canFreeze$;
    this.contentType$ = this.notebookStateService.contentType$;
    this.isExportDatasetsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookExportDatasets);
    this.isCellExported$ = this.notebookStateService.getIsCellExported$(this.cellToken);
    this.canDelete$ = this.notebookStateService.canDelete$;

    this.cellExportFacade.getDatasetResults().subscribe((results) => {
      results = results.filter((r) => r.producerToken === this.cellToken);
      this.exportedDatasetResult = results.length > 0 ? results[0] : null;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectCell() {
    this.notebookCellFacade.selectCell(this.cellToken, true);
  }

  runCell({
    cellToken,
    contentType,
    nextCellToken,
  }: {
    cellToken: string;
    contentType: NotebookTypes.ContentType;
    nextCellToken: string;
  }) {
    this.notebookCellFacade.runCell(this.notebookToken, this.cellToken);
    if (!nextCellToken) this.notebookCellFacade.addCell({ notebookToken: this.notebookToken });
    else this.notebookCellFacade.selectCell(nextCellToken, false);
  }

  moveCell(direction: 'up' | 'down') {
    this.notebookCellFacade.moveCell(this.cellToken, direction === 'down');
  }

  deleteCell() {
    this.notebookCellFacade.removeCell(this.notebookToken, this.cellToken);
  }

  insertCellAbove() {
    this.notebookCellFacade.addCell({ notebookToken: this.notebookToken, nextToken: this.cellToken });
  }

  insertCellBelow(nextCellToken: string) {
    this.notebookCellFacade.addCell({ notebookToken: this.notebookToken, nextToken: nextCellToken });
  }

  toggleFreezeCell() {
    this.notebookCellFacade.toggleFreezeCell(this.cellToken);
  }

  toggleFoldCell() {
    this.notebookCellFacade.foldCell(this.cellToken);
  }

  toggleIncludedInReportState(state: boolean) {
    if (state) this.notebookCellFacade.removeCellFromLayout(this.cellToken);
    else this.notebookCellFacade.addCellToLayout(this.cellToken);
  }

  updateContentType(cellType: NotebookTypes.ContentType) {
    this.notebookCellFacade.updateContentType(this.cellToken, cellType);
  }

  goToDataset(exportedDatasetResult: DataViewTypes.DatasetResult) {
    if (!exportedDatasetResult) return;
    else if (this.$state.includes('**.reports.edit') || this.$state.includes('**.reports.edit.**')) {
      this.$state.go('application.reports.edit.datasets', {
        switchviewToken: exportedDatasetResult.switchViewToken,
      });
    } else if (this.$state.includes('reportShow.noRuns')) {
      this.$state.go('reportShow.noRuns.detailsView.detailsDataset', {
        queryId: exportedDatasetResult.queryToken,
      });
    } else {
      this.$state.go('reportShow.runs.detailsView.detailsDataset', {
        queryId: exportedDatasetResult.queryToken,
      });
    }
  }

  onResultCellClick() {
    this.isSelected$.pipe(take(1)).subscribe((isSelected) => {
      if (!isSelected) {
        this.selectCell();
      }
    });
  }

  trackByToken(index: number, item: any): string {
    // console.log(item,' trackByToken called');
    return item.token;
  }
}
